import { HttpClient } from '@angular/common/http';
import {
  Component,
  input,
  computed,
  inject,
  signal,
  effect,
  DestroyRef,
} from '@angular/core';
import { ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { catchError, map, of } from 'rxjs';

import { IconType } from '@shared-enums';

@Component({
  selector: 'v-one-icon',
  imports: [],
  templateUrl: './v-one-icon.component.html',
  styleUrls: ['./v-one-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneIconComponent {
  name = input.required<string>();

  type = input<IconType>(IconType.OUTLINE);
  color = input<string>('currentColor');
  className = input<string>('');

  readonly svgContent = signal<SafeHtml | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly hasError = signal<boolean>(false);

  readonly iconPath = computed(() => {
    const iconName = this.name();
    const iconType = this.type();
    return `assets/uplift/icons/${iconName}-${iconType}.svg`;
  });

  readonly iconClasses = computed(() => {
    const baseClass = 'v-one-icon';
    const typeClass = `${baseClass}--${this.type()}`;
    const customClass = this.className();
    const errorClass = this.hasError() ? `${baseClass}--error` : '';

    return [baseClass, typeClass, customClass, errorClass]
      .filter(Boolean)
      .join(' ');
  });

  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Effect to reload icon when path changes
    effect(() => {
      // Access the iconPath to create a dependency
      const path = this.iconPath();
      // Only reload if we're not in the initial state
      if (path) {
        this.loadIcon();
      }
    });
  }

  private loadIcon(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    const iconPath = this.iconPath();

    this.http
      .get(iconPath, { responseType: 'text' })
      .pipe(
        map((svgText) => {
          const iconType = this.type();
          const iconName = this.name();
          let modifiedSvg = svgText;

          // For outline icons
          if (iconType === IconType.OUTLINE) {
            // Replace stroke colors
            modifiedSvg = svgText.replace(
              /stroke="[^"]*"/g,
              'stroke="currentColor"',
            );

            // Some outline icons (like error) use fill instead of stroke
            // Only replace fill for specific icons that don't have stroke
            if (iconName === 'error') {
              modifiedSvg = modifiedSvg.replace(
                /fill="[^"]*"/g,
                'fill="currentColor"',
              );
            }
          }
          // For filled icons, replace fill with currentColor
          else if (iconType === IconType.FILLED) {
            modifiedSvg = svgText.replace(
              /fill="(?!white|#[fF]{6}|#[fF]{3})([^"]*)"/g,
              'fill="currentColor"',
            );
            modifiedSvg = modifiedSvg.replace(
              /stroke="(?!white|#[fF]{6}|#[fF]{3})([^"]*)"/g,
              'stroke="currentColor"',
            );
          }
          modifiedSvg = modifiedSvg.replace(/<svg([^>]*)>/, (match, attrs) => {
            let nextAttrs = /width="[^"]*"/.test(attrs)
              ? attrs.replace(/width="[^"]*"/, 'width="100%"')
              : `${attrs} width="100%"`;
            nextAttrs = /height="[^"]*"/.test(nextAttrs)
              ? nextAttrs.replace(/height="[^"]*"/, 'height="100%"')
              : `${nextAttrs} height="100%"`;
            return `<svg${nextAttrs}>`;
          });

          return this.sanitizer.bypassSecurityTrustHtml(modifiedSvg);
        }),
        catchError(() => {
          this.hasError.set(true);
          console.warn(`Icon not found: ${iconPath}`);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sanitizedSvg) => {
        this.svgContent.set(sanitizedSvg);
        this.isLoading.set(false);
      });
  }
}
